/**
 * Spatial hash grid for efficient proximity queries
 * Used for combat target finding and collision detection
 * Optimized to reduce allocations and avoid string concatenation
 */

export interface Positioned {
  x: number;
  z: number;
}

// Use numeric keys instead of string concatenation for performance
function packKey(cx: number, cz: number): number {
  // Pack two 16-bit signed integers into one 32-bit number
  // Supports coordinates from -32768 to 32767
  return ((cx + 32768) << 16) | ((cz + 32768) & 0xFFFF);
}

export class SpatialHash<T extends Positioned> {
  private cellSize: number;
  private invCellSize: number; // Pre-computed inverse for faster division
  private grid: Map<number, T[]> = new Map();

  // Reusable arrays to reduce allocations
  private queryResultPool: T[][] = [];
  private maxPoolSize = 10;

  constructor(cellSize: number = 5) {
    this.cellSize = cellSize;
    this.invCellSize = 1 / cellSize;
  }

  private getCellCoords(x: number, z: number): [number, number] {
    return [
      Math.floor(x * this.invCellSize),
      Math.floor(z * this.invCellSize)
    ];
  }

  private getCellKey(x: number, z: number): number {
    const cx = Math.floor(x * this.invCellSize);
    const cz = Math.floor(z * this.invCellSize);
    return packKey(cx, cz);
  }

  clear(): void {
    // Return arrays to pool before clearing
    for (const arr of this.grid.values()) {
      if (this.queryResultPool.length < this.maxPoolSize) {
        arr.length = 0;
        this.queryResultPool.push(arr);
      }
    }
    this.grid.clear();
  }

  insert(item: T): void {
    const key = this.getCellKey(item.x, item.z);
    let cell = this.grid.get(key);
    if (!cell) {
      // Reuse pooled array if available
      cell = this.queryResultPool.pop() || [];
      this.grid.set(key, cell);
    }
    cell.push(item);
  }

  remove(item: T): void {
    const key = this.getCellKey(item.x, item.z);
    const cell = this.grid.get(key);
    if (cell) {
      const idx = cell.indexOf(item);
      if (idx !== -1) {
        // Swap with last element and pop (O(1) removal)
        const last = cell.pop()!;
        if (idx < cell.length) {
          cell[idx] = last;
        }
      }
      if (cell.length === 0) {
        this.grid.delete(key);
        if (this.queryResultPool.length < this.maxPoolSize) {
          this.queryResultPool.push(cell);
        }
      }
    }
  }

  update(item: T, oldX: number, oldZ: number): void {
    const oldKey = this.getCellKey(oldX, oldZ);
    const newKey = this.getCellKey(item.x, item.z);

    if (oldKey !== newKey) {
      this.remove({ x: oldX, z: oldZ } as T);
      this.insert(item);
    }
  }

  queryRadius(x: number, z: number, radius: number): T[] {
    const results: T[] = [];
    const radiusSq = radius * radius;

    const minCx = Math.floor((x - radius) * this.invCellSize);
    const maxCx = Math.floor((x + radius) * this.invCellSize);
    const minCz = Math.floor((z - radius) * this.invCellSize);
    const maxCz = Math.floor((z + radius) * this.invCellSize);

    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cz = minCz; cz <= maxCz; cz++) {
        const cell = this.grid.get(packKey(cx, cz));
        if (cell) {
          for (let i = 0, len = cell.length; i < len; i++) {
            const item = cell[i];
            const dx = item.x - x;
            const dz = item.z - z;
            if (dx * dx + dz * dz <= radiusSq) {
              results.push(item);
            }
          }
        }
      }
    }

    return results;
  }

  // Optimized version that takes a callback instead of returning array
  queryRadiusCallback(x: number, z: number, radius: number, callback: (item: T) => void): void {
    const radiusSq = radius * radius;

    const minCx = Math.floor((x - radius) * this.invCellSize);
    const maxCx = Math.floor((x + radius) * this.invCellSize);
    const minCz = Math.floor((z - radius) * this.invCellSize);
    const maxCz = Math.floor((z + radius) * this.invCellSize);

    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cz = minCz; cz <= maxCz; cz++) {
        const cell = this.grid.get(packKey(cx, cz));
        if (cell) {
          for (let i = 0, len = cell.length; i < len; i++) {
            const item = cell[i];
            const dx = item.x - x;
            const dz = item.z - z;
            if (dx * dx + dz * dz <= radiusSq) {
              callback(item);
            }
          }
        }
      }
    }
  }

  queryNearest(x: number, z: number, maxRadius: number, filter?: (item: T) => boolean): T | null {
    let nearest: T | null = null;
    let nearestDistSq = maxRadius * maxRadius;

    const minCx = Math.floor((x - maxRadius) * this.invCellSize);
    const maxCx = Math.floor((x + maxRadius) * this.invCellSize);
    const minCz = Math.floor((z - maxRadius) * this.invCellSize);
    const maxCz = Math.floor((z + maxRadius) * this.invCellSize);

    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cz = minCz; cz <= maxCz; cz++) {
        const cell = this.grid.get(packKey(cx, cz));
        if (cell) {
          for (let i = 0, len = cell.length; i < len; i++) {
            const item = cell[i];
            if (filter && !filter(item)) continue;

            const dx = item.x - x;
            const dz = item.z - z;
            const distSq = dx * dx + dz * dz;

            if (distSq < nearestDistSq) {
              nearestDistSq = distSq;
              nearest = item;
            }
          }
        }
      }
    }

    return nearest;
  }
}
